import { createSlice, PayloadAction } from "@reduxjs/toolkit";
import { Product, PaginatedProductResponse } from "../../types/pages/products";

interface ProductsState {
  products: Product[];
  loading: boolean;
  error: string | null;
  addProductLoading: boolean;
  addProductError: string | null;
  addProductSuccess: boolean;
  editProductLoading: boolean;
  editProductError: string | null;
  editProductSuccess: boolean;
  deleteProductLoading: boolean;
  deleteProductError: string | null;
  deleteProductSuccess: boolean;
  selectedProduct: Product | null;
  fetchProductLoading: boolean;
  fetchProductError: string | null;
  totalCount: number;
  page: number;
  rowsPerPage: number;
  sortBy: string;
  sortOrder: number;
}

const initialState: ProductsState = {
  products: [],
  loading: false,
  error: null,
  addProductLoading: false,
  addProductError: null,
  addProductSuccess: false,
  editProductLoading: false,
  editProductError: null,
  editProductSuccess: false,
  deleteProductLoading: false,
  deleteProductError: null,
  deleteProductSuccess: false,
  selectedProduct: null,
  fetchProductLoading: false,
  fetchProductError: null,
  totalCount: 0,
  page: 0,
  rowsPerPage: 10,
  sortBy: "created_at",
  sortOrder: -1,
};

const productsSlice = createSlice({
  name: "products",
  initialState,
  reducers: {
    fetchProducts: (
      state,
      action: PayloadAction<{ page: number; rowsPerPage: number; sortBy?: string; sortOrder?: number }>
    ) => { },
    fetchProductsStart: (state) => {
      state.loading = true;
      state.error = null;
    },
    fetchProductsSuccess: (
      state,
      action: PayloadAction<PaginatedProductResponse>
    ) => {
      state.loading = false;
      state.products = action.payload.data;
      state.totalCount = action.payload.total;
    },
    fetchProductsFailure: (state, action: PayloadAction<string>) => {
      state.loading = false;
      state.error = action.payload;
    },
    setPage: (state, action: PayloadAction<number>) => {
      state.page = action.payload;
    },
    setRowsPerPage: (state, action: PayloadAction<number>) => {
      state.rowsPerPage = action.payload;
      state.page = 0;
    },
    setSortBy: (state, action: PayloadAction<string>) => {
      state.sortBy = action.payload;
      state.page = 0;
    },
    setSortOrder: (state, action: PayloadAction<number>) => {
      state.sortOrder = action.payload;
      state.page = 0;
    },
    // Add Product
    addProduct: (
      state,
      action: PayloadAction<
        Omit<Product, "_id" | "created_at" | "updated_at" | "organization">
      >
    ) => { },
    addProductStart: (state) => {
      state.addProductLoading = true;
      state.addProductError = null;
      state.addProductSuccess = false;
    },
    addProductSuccess: (state, action: PayloadAction<Product>) => {
      state.addProductLoading = false;
      state.addProductSuccess = true;
      state.products.push(action.payload);
    },
    addProductFailure: (state, action: PayloadAction<string>) => {
      state.addProductLoading = false;
      state.addProductError = action.payload;
      state.addProductSuccess = false;
    },
    // Edit Product
    editProduct: (
      state,
      action: PayloadAction<{
        productId: string;
        productData: Partial<
          Omit<Product, "_id" | "created_at" | "updated_at" | "organization">
        >;
      }>
    ) => { },
    editProductStart: (state) => {
      state.editProductLoading = true;
      state.editProductError = null;
      state.editProductSuccess = false;
    },
    editProductSuccess: (state, action: PayloadAction<Product>) => {
      state.editProductLoading = false;
      state.editProductSuccess = true;
      const index = state.products.findIndex((p) => p._id === action.payload._id);
      if (index !== -1) {
        state.products[index] = action.payload;
      }
    },
    editProductFailure: (state, action: PayloadAction<string>) => {
      state.editProductLoading = false;
      state.editProductError = action.payload;
      state.editProductSuccess = false;
    },
    deleteProduct: (state, action: PayloadAction<string>) => { },
    deleteProductStart: (state) => {
      state.deleteProductLoading = true;
      state.deleteProductError = null;
      state.deleteProductSuccess = false;
    },
    deleteProductSuccess: (state, action: PayloadAction<string>) => {
      state.deleteProductLoading = false;
      state.deleteProductSuccess = true;
      state.products = state.products.filter((p) => p._id !== action.payload);
    },
    deleteProductFailure: (state, action: PayloadAction<string>) => {
      state.deleteProductLoading = false;
      state.deleteProductError = action.payload;
      state.deleteProductSuccess = false;
    },
    clearProductsError: (state) => {
      state.error = null;
      state.addProductError = null;
      state.addProductSuccess = false;
      state.editProductError = null;
      state.editProductSuccess = false;
      state.deleteProductError = null;
      state.deleteProductSuccess = false;
    },
    fetchProductById: (state, action: PayloadAction<string>) => { },
    fetchProductStart: (state) => {
      state.fetchProductLoading = true;
      state.fetchProductError = null;
    },
    fetchProductSuccess: (state, action: PayloadAction<Product>) => {
      state.fetchProductLoading = false;
      state.selectedProduct = action.payload;
    },
    fetchProductFailure: (state, action: PayloadAction<string>) => {
      state.fetchProductLoading = false;
      state.fetchProductError = action.payload;
    },
    clearSelectedProduct: (state) => {
      state.selectedProduct = null;
      state.fetchProductError = null;
    },
  },
});

export const {
  fetchProducts,
  fetchProductsStart,
  fetchProductsSuccess,
  fetchProductsFailure,
  addProduct,
  addProductStart,
  addProductSuccess,
  addProductFailure,
  editProduct,
  editProductStart,
  editProductSuccess,
  editProductFailure,
  deleteProduct,
  deleteProductStart,
  deleteProductSuccess,
  deleteProductFailure,
  clearProductsError,
  fetchProductById,
  fetchProductStart,
  fetchProductSuccess,
  fetchProductFailure,
  clearSelectedProduct,
  setPage,
  setRowsPerPage,
  setSortBy,
  setSortOrder,
} = productsSlice.actions;
export default productsSlice.reducer;
